import { css } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import dayjs from 'dayjs';
import { startCase } from 'lodash-es';
import { OfficeUserName, generateTrackingId } from '@frontend/call-intel-core';
import { BadgeBanner } from '@frontend/components';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Accordion, Button, Chip, NakedUl, Text } from '@frontend/design-system';
import { getFullName, getOfficeUser, getStatusChipColor } from '../../utils/call-insight-panel-util';
import { PhoneTreePreview } from '../phone-tree-preview';

type CallDetailsSectionProps = {
  call: HydratedCallRecord;
  onClick: () => void;
};

export const CallDetailsSection = ({ call, onClick }: CallDetailsSectionProps) => {
  const { t } = useTranslation('call-insight-panel');

  const { getScopeName, isSingleTypeScope } = useAppScopeStore();

  const { startedAt, direction, status = '', locationId, callerNumber, dialedNumber, user, phoneTrees } = call;

  const statusChipColor = getStatusChipColor(status);

  const isMulti = !isSingleTypeScope;

  const isOutboundCall = direction === 'outbound';

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isCallPathMappingEnabled = getFeatureFlagValue('call-path-mapping');

  return (
    <>
      <Accordion.Header title={t('About This Call')} />
      <Accordion.Body>
        <NakedUl css={styles.list}>
          <li>
            <Text weight='bold'>{t('Call Details')}</Text>
          </li>

          {isMulti && locationId && (
            <li>
              <Text size='medium' color='subdued'>
                {t('Location')}
              </Text>
              <Chip.Location maxWidth='300'>{getScopeName(locationId)}</Chip.Location>
            </li>
          )}

          <li>
            <Text size='medium' color='subdued'>
              {t('Time')}
            </Text>
            <Text size='medium'>{dayjs(startedAt).format('MMM D, YYYY, h:mm A')}</Text>
          </li>

          <li>
            <Text size='medium' color='subdued'>
              {t('Result')}
            </Text>
            {direction === 'outbound' && <span>-</span>}
            {direction === 'inbound' && (
              <Chip isResponsive variant={statusChipColor}>
                {startCase(status)}
              </Chip>
            )}
            {!direction && <span>-</span>}
          </li>

          {!isOutboundCall && <Divider />}

          {!isOutboundCall && (
            <li>
              <Text weight='bold'>{t('Call Path Overview')}</Text>
            </li>
          )}

          <li>
            <Text size='medium' color='subdued'>
              {t('Office Number')}
            </Text>
            <Text size='medium'>
              {direction
                ? formatPhoneNumber(direction === 'outbound' ? callerNumber : dialedNumber)
                : formatPhoneNumber(call.callIntelInsights?.officeNumber?.nationalNumber?.toString())}
            </Text>
          </li>

          <li>
            <Text size='medium' color='subdued'>
              {t('Office User')}
            </Text>
            <OfficeUserName
              displayName={getOfficeUser(call, t('Unknown'))}
              profileName={getFullName(user?.firstName, user?.lastName, t('Unknown'))}
              userId={user?.userId || ''}
            />
          </li>

          {isCallPathMappingEnabled && !isOutboundCall && !!phoneTrees && phoneTrees.length > 0 && (
            <li>
              <Text size='medium' color='subdued'>
                {t('Phone Tree')}
              </Text>
              <PhoneTreePreview ivrEvents={phoneTrees} />
            </li>
          )}

          {isCallPathMappingEnabled && !isOutboundCall && (
            <>
              <li>
                <BadgeBanner borderColor={theme.colors.indigo50} backgroundColor={theme.colors.indigo5}>
                  <BadgeBanner.AnimatedBadge
                    animated={false}
                    bgColor={theme.colors.indigo50}
                    label={t('Beta')}
                  ></BadgeBanner.AnimatedBadge>
                  <BadgeBanner.Title>
                    {t('To see a step-by-step breakdown of this call, click “View Call Path.”')}
                  </BadgeBanner.Title>
                </BadgeBanner>
              </li>
              <li>
                <Button
                  onClick={onClick}
                  disabled={isOutboundCall}
                  variant='tertiary'
                  size='large'
                  label={t('View Call Path')}
                  showLabelWhenDisabled
                  hoverLabel={isOutboundCall && t('Not available for outbound calls.')}
                  trackingId={generateTrackingId({
                    prefix: 'CallInsights',
                    component: 'AboutThisCall',
                    context: 'ViewCallPath',
                  })}
                >
                  {t('View Call Path')}
                </Button>
              </li>
            </>
          )}
        </NakedUl>
      </Accordion.Body>
    </>
  );
};

const styles = {
  list: css`
    li {
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin-bottom: ${theme.spacing(2)};

      .data-label {
        color: ${theme.colors.neutral50};
        text-wrap: nowrap;
      }
    }

    li:last-child {
      margin-bottom: ${theme.spacing(0)};
    }
  `,
};
